import Transformer from '@/pixi/Transformer'
import * as PIXI from 'pixi.js'
import { DisplayObjectState } from '@/types'
import { DisplayObjectPair } from '@/pixi/types'
import { applyAxisSnapToSprite, destroyTransformerAxisLines } from '@/pixi/snapPositioning'
import { Resolution } from '@/types'

let updateTransformStateTimeout: number | null = null

export default function useDisplayObject () {
  const onTransformDisplayObject = (
    dispObjState: DisplayObjectState,
    textDisplayObjectsMap: Map<number, DisplayObjectPair>,
    stickerDisplayObjectsMap: Map<number, DisplayObjectPair>,
    transformerLayer: PIXI.Container,
    isTransformerHandleGrabbed: boolean,
    resolution: Resolution
  ): void =>  {
    const sprite = getDisplayObjectSprite(dispObjState, textDisplayObjectsMap, stickerDisplayObjectsMap)
    if (sprite) {
      if (!isTransformerHandleGrabbed) {
        applyAxisSnapToSprite(sprite, resolution).forEach(transformerAxisLine => {
          transformerLayer.addChild(transformerAxisLine)
        })
      }
      if (updateTransformStateTimeout === null) {
        // Limit state update to animation rate
        updateTransformStateTimeout = requestAnimationFrame(() => {
          // Update display object location state
          updateTransformStateTimeout = null
          updateDisplayObjState(dispObjState, textDisplayObjectsMap, stickerDisplayObjectsMap)
        })
      }
    }
  }

  const getDisplayObjectSprite = (
    dispObjState: DisplayObjectState,
    textDisplayObjectsMap: Map<number, DisplayObjectPair>,
    stickerDisplayObjectsMap: Map<number, DisplayObjectPair>
  ): PIXI.Sprite | undefined => {
    if (textDisplayObjectsMap.has(dispObjState.id)) {
      return (textDisplayObjectsMap.get(dispObjState.id) as DisplayObjectPair).sprite
    } else if (stickerDisplayObjectsMap.has(dispObjState.id)) {
      return (stickerDisplayObjectsMap.get(dispObjState.id) as DisplayObjectPair).sprite
    }
  }

  const updateDisplayObjState = (
    dispObjState: DisplayObjectState,
    textDisplayObjectsMap: Map<number, DisplayObjectPair>,
    stickerDisplayObjectsMap: Map<number, DisplayObjectPair>
  ): void => {
    const sprite = getDisplayObjectSprite(dispObjState, textDisplayObjectsMap, stickerDisplayObjectsMap)

    if (sprite) {
      dispObjState.x = sprite.position.x
      dispObjState.y = sprite.position.y
      dispObjState.scale = sprite.scale.x
    }
  }

  const moveElementOnCanvasResize = (
    transformer: Transformer,
    oldCanvasWidth: number,
    oldCanvasHeight: number,
    application: PIXI.Application
  ) => {
    const bounds = transformer.getBounds()
    // Calculate the middle point of the sprite
    const centerX = bounds.x + bounds.width / 2
    const centerY = bounds.y + bounds.height / 2

    const xRatio = centerX / oldCanvasWidth
    const yRatio = centerY / oldCanvasHeight

    // Set position based on the new canvas size, keeping the original centre position proportionally
    const xPos = xRatio * application.renderer.width - bounds.width / 2
    const yPos = yRatio * application.renderer.height - bounds.height / 2

    const delta = new PIXI.Point(xPos - bounds.x, yPos - bounds.y)
    // Translates the sprite and handles when its outside the canvas bounds
    transformer.translateGroup(delta)
    destroyTransformerAxisLines()
  }

  return {
    moveElementOnCanvasResize,
    onTransformDisplayObject,
    updateDisplayObjState,
    getDisplayObjectSprite
  }
}
